import type { Map as MapLibreMap } from 'maplibre-gl';

/** Wheel events closer together than this, ms, belong to one gesture (wheel ticks, trackpad momentum). */
const GESTURE_GAP_MS = 250;

/**
 * Keeps a wheel zoom going when the pointer slides over a popup or control mid-gesture.
 *
 * MapLibre puts popups and controls in the map container but only listens for wheel events on the canvas
 * container below it, so a scroll that reaches a popup used to stop zooming the map. A gesture that started
 * on the map is now handed to the canvas wherever it continues; one that starts on a popup stays there and
 * scrolls its content as usual.
 */
export function forwardMapWheel(map: MapLibreMap): () => void {
  const container = map.getContainer();
  const canvasContainer = map.getCanvasContainer();
  let lastTime = -Infinity;
  let startedOnMap = false;

  const onWheel = (e: WheelEvent) => {
    const onMap = canvasContainer.contains(e.target as Node);
    if (e.timeStamp - lastTime > GESTURE_GAP_MS) startedOnMap = onMap;
    lastTime = e.timeStamp;
    if (onMap || !startedOnMap) return;

    e.preventDefault(); // don't scroll the popup underneath
    e.stopPropagation();
    map.getCanvas().dispatchEvent(
      new WheelEvent('wheel', {
        bubbles: true,
        cancelable: true,
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaZ: e.deltaZ,
        deltaMode: e.deltaMode,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        ctrlKey: e.ctrlKey, // trackpad pinch
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
      }),
    );
  };

  // Capture, so this runs before popup content sees (and maybe scrolls on) the event.
  container.addEventListener('wheel', onWheel, { capture: true, passive: false });
  return () => container.removeEventListener('wheel', onWheel, { capture: true });
}
